import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { getReservationReports } from '../../api/endpoints/reports';
import type { ReservationReportRow } from '../../api/types/reports';
import type { BookingStatus } from '../../api/types/common';
import { ReservationIdentifierLink } from '../../components/ReservationIdentifierLink';
import { DataTable, EmptyState, PageHeader, QueryBoundary, type DataTableColumn } from '../../components/common';
import { ReservationStatsSection } from './ReservationStatsSection';

const RECENT_LIMIT = 5;

type RecentRow = ReservationReportRow;

/**
 * Recent-activity widget over the shared `Report/Reservations` endpoint (see
 * api/endpoints/reports.ts), fixed to `providerType: 'Air'`, last 90 days. `status`
 * selects which of the two Air lists this mirrors: `'Prebooked'` for "Uçak
 * Rezervasyonları", `'Booked'` for "Uçak Biletleri". The identifier column follows the
 * same PNR-vs-ticket split as the full list pages, linking into `ReservationDetailPage`.
 */
function RecentAirCard({
  status,
  title,
  identifierField,
}: {
  status: BookingStatus;
  title: string;
  identifierField: 'pnr' | 'ticket';
}) {
  const query = useQuery({
    queryKey: ['dashboard', 'recentAir', status],
    queryFn: () =>
      getReservationReports({
        insertStartDate: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
        insertEndDate: dayjs().format('YYYY-MM-DD'),
        status,
        providerType: 'Air',
      }),
  });

  const rows: RecentRow[] = [...(query.data?.reservationReports ?? [])]
    .sort((a, b) => {
      const parse = (v?: string) => (v ? dayjs(v, 'DD.MM.YYYY HH:mm:ss') : null);
      const da = parse(a.bookedAt || a.prebookedAt);
      const db = parse(b.bookedAt || b.prebookedAt);
      if (!da?.isValid()) return 1;
      if (!db?.isValid()) return -1;
      return db.valueOf() - da.valueOf();
    })
    .slice(0, RECENT_LIMIT);

  const columns: DataTableColumn<RecentRow>[] = [
    {
      id: 'id',
      header: identifierField === 'ticket' ? 'Bilet No' : 'PNR',
      cell: (row) => (
        <ReservationIdentifierLink row={row} detailBasePath="/air/reservations" field={identifierField} />
      ),
    },
    { id: 'pax', header: 'Yolcu', cell: (row) => [row.firstName, row.lastName].filter(Boolean).join(' ') || '—' },
    {
      id: 'route',
      header: 'Güzergah',
      cell: (row) => [row.origin, row.destination].filter(Boolean).join(' → ') || '—',
    },
    { id: 'fare', header: 'Ücret', cell: (row) => row.totalFare ?? '—' },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          {title}
        </Typography>
        <QueryBoundary
          query={query}
          skeleton="table"
          skeletonRows={RECENT_LIMIT}
          isEmpty={() => rows.length === 0}
          emptyState={<EmptyState title="Bu aralıkta kayıt bulunamadı." />}
        >
          {() => (
            <DataTable
              columns={columns}
              rows={rows}
              getRowKey={(row) =>
                (row.shoppingCartId as string | undefined) ?? `${row.origin}-${row.destination}-${row.bookedAt ?? row.prebookedAt ?? ''}`
              }
            />
          )}
        </QueryBoundary>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  return (
    <Box>
      <PageHeader title="Anasayfa" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentAirCard status="Prebooked" title="Uçak Rezervasyonları" identifierField="pnr" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentAirCard status="Booked" title="Uçak Biletleri" identifierField="ticket" />
        </Grid>
      </Grid>

      <ReservationStatsSection />
    </Box>
  );
}
